import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, Clock, Filter, RefreshCw } from "lucide-react";

const STAGE_BADGE = {
  draft: "bg-slate-200 text-slate-700", pending_org: "bg-orange-100 text-orange-700",
  pending_cbr: "bg-blue-100 text-blue-700", correction_required: "bg-red-100 text-red-700",
  corrected: "bg-indigo-100 text-indigo-700", approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-slate-300 text-slate-700",
};
const VAL_ICON = {
  clean: <CheckCircle2 className="w-4 h-4 text-emerald-500" />,
  warning: <AlertTriangle className="w-4 h-4 text-amber-500" />,
  error: <XCircle className="w-4 h-4 text-red-500" />,
};

const SORTS = [["submitted_desc", "Newest"], ["submitted_asc", "Oldest"], ["game_date", "Game Date"], ["warnings", "Warning Count"], ["organization", "Organization"], ["tournament", "Tournament"]];
const STAGES = [["pending", "Pending Only"], ["pending_cbr", "Pending CBR"], ["pending_org", "Pending Org"], ["correction_required", "Correction"], ["approved", "Approved"], ["rejected", "Rejected"], ["draft", "Draft"]];

export default function AdminApprovalQueue() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("pending");
  const [sort, setSort] = useState("submitted_desc");
  const [gameType, setGameType] = useState("");
  const [province, setProvince] = useState("");
  const [bulking, setBulking] = useState(false);

  const bulkApprove = async () => {
    if (!window.confirm("Approve ALL pending games that have zero warnings and zero errors?")) return;
    setBulking(true);
    try {
      const { data } = await api.post("/live/admin/games/bulk-approve-clean");
      toast.success(`${data.approved} approved · ${data.skipped} skipped (had warnings/errors)`);
      window.dispatchEvent(new Event("cbr:approvals-changed"));
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBulking(false); }
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = { sort, limit: 200 };
    if (stage) params.stage = stage;
    if (gameType) params.game_type = gameType;
    if (province) params.province = province;
    api.get("/live/admin/games/approval-queue", { params })
      .then(({ data }) => { setRows(data.items || []); setTotal(data.total || 0); })
      .catch((e) => formatApiError(e))
      .finally(() => setLoading(false));
  }, [stage, sort, gameType, province]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto p-6" data-testid="admin-approval-queue">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="flex items-center gap-2 label-overline text-[#E63946]"><Clock className="w-4 h-4" /> Approval Workflow</div>
            <h1 className="font-display text-3xl uppercase tracking-tight">Game Approval Queue</h1>
            <p className="text-sm text-slate-500 mt-1">{total} game{total === 1 ? "" : "s"} · review a game in under 60 seconds.</p>
          </div>
          <button data-testid="queue-bulk-approve" onClick={bulkApprove} disabled={bulking} className="inline-flex items-center gap-2 bg-emerald-600 text-white px-3 py-2 rounded-sm text-xs font-semibold uppercase tracking-wider disabled:opacity-50"><CheckCircle2 className="w-4 h-4" /> {bulking ? "Approving…" : "Bulk Approve Clean"}</button>
          <button data-testid="queue-refresh" onClick={load} className="inline-flex items-center gap-2 border border-slate-300 px-3 py-2 rounded-sm text-xs font-semibold uppercase tracking-wider"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh</button>
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-4 bg-white border border-slate-200 rounded-sm p-3">
          <Sel testid="queue-filter-stage" label="Stage" value={stage} onChange={setStage}>
            <option value="">All</option>{STAGES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </Sel>
          <Sel testid="queue-sort" label="Sort" value={sort} onChange={setSort}>{SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Sel>
          <Sel testid="queue-filter-gametype" label="Game Type" value={gameType} onChange={setGameType}>
            <option value="">All</option>{["tournament", "league", "playoff", "exhibition", "scrimmage", "showcase"].map((g) => <option key={g} value={g}>{g}</option>)}
          </Sel>
          <Sel testid="queue-filter-province" label="Province" value={province} onChange={setProvince}>
            <option value="">All</option>{["ON", "QC", "BC", "AB", "MB", "SK", "NS", "NB", "NL", "PE"].map((p) => <option key={p} value={p}>{p}</option>)}
          </Sel>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          {loading ? (
            <div className="p-10 label-overline text-slate-500">Loading queue…</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400" />
              <div className="font-display text-2xl uppercase mt-4">Queue is clear</div>
              <p className="text-sm text-slate-600 mt-2">No games match this filter.</p>
            </div>
          ) : (
            <table className="w-full text-sm min-w-max">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  {["Val", "Game Date", "Matchup", "Tournament", "Score", "Type", "Submitted By", "Stage", "Submitted"].map((h) => <th key={h} className="p-3 label-overline">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.game_id} data-testid={`queue-row-${r.game_id}`} onClick={() => navigate(`/admin/games/approval/${r.game_id}`)} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer">
                    <td className="p-3" title={`${r.validation?.errors || 0} errors · ${r.validation?.warnings || 0} warnings`}>
                      <span className="inline-flex items-center gap-1">{VAL_ICON[r.validation?.status] || VAL_ICON.clean}{r.validation?.warnings > 0 && <span className="text-[10px] text-amber-600 font-bold">{r.validation.warnings}</span>}</span>
                    </td>
                    <td className="p-3 font-data">{r.date || "—"}</td>
                    <td className="p-3 font-semibold text-[#0A192F]">{r.home_team} <span className="text-slate-400">vs</span> {r.away_team}</td>
                    <td className="p-3 text-slate-600">{r.tournament || "—"}</td>
                    <td className="p-3 font-data">{r.final_score}</td>
                    <td className="p-3 text-slate-500 uppercase text-xs">{r.game_type || "—"}</td>
                    <td className="p-3 text-slate-600">{r.submitted_by || "—"}</td>
                    <td className="p-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm ${STAGE_BADGE[r.approval_status] || "bg-slate-100"}`}>{r.approval_stage_label}</span></td>
                    <td className="p-3 text-xs text-slate-400">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString("en-CA") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function Sel({ label, value, onChange, children, testid }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="label-overline text-[10px]">{label}</span>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white min-w-[130px]">{children}</select>
    </label>
  );
}
